import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import casesService, {
  Case,
  CaseCreateInput,
  CaseUpdateInput,
} from "../../services/casesService";

interface CasesState {
  cases: Case[];
  currentCase: Case | null;
  loading: boolean;
  error: string | null;
}

const initialState: CasesState = {
  cases: [],
  currentCase: null,
  loading: false,
  error: null,
};

export const fetchCases = createAsyncThunk(
  "cases/fetchCases",
  async (params: {
    userId: string;
    page?: number;
    limit?: number;
    filters?: Record<string, any>;
  }) => {
    const response = await casesService.getCases(
      params.userId,
      params.page,
      params.limit,
      params.filters,
    );
    return response;
  },
);

export const fetchCaseById = createAsyncThunk(
  "cases/fetchCaseById",
  async (params: { orderId: string; userId: string }) => {
    const response = await casesService.getCaseById(
      params.orderId,
      params.userId,
    );
    return response;
  },
);

export const createCase = createAsyncThunk(
  "cases/createCase",
  async (caseData: CaseCreateInput, { rejectWithValue }) => {
    try {
      const response = await casesService.createCase(caseData);
      return response;
    } catch (error: any) {
      // Carry the backend's `{ message, errors }` body through as the
      // rejected payload (instead of RTK's default serialized-error, which
      // drops it) so callers' `.unwrap()` catch can read field-level
      // validation errors from a 422.
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  },
);

export const updateCase = createAsyncThunk(
  "cases/updateCase",
  async (caseData: CaseUpdateInput, { rejectWithValue }) => {
    try {
      const response = await casesService.updateCase(caseData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  },
);

export const deleteCase = createAsyncThunk(
  "cases/deleteCase",
  async (params: { orderId: string; userId: string }, { rejectWithValue }) => {
    try {
      await casesService.deleteCase(params.orderId, params.userId);
      return params.orderId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  },
);

export const updateCaseStatus = createAsyncThunk(
  "cases/updateStatus",
  async (
    caseData: {
      status: string;
      id?: number;
      order_id?: string;
      user_id?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await casesService.updateCaseStatus(caseData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  },
);

export const fetchPublicCaseDetails = createAsyncThunk(
  "cases/fetchPublicCaseDetails",
  async (params: { caseId: number; firmId: number; accessCode: string }) => {
    const response = await casesService.getPublicCaseDetails(
      params.caseId,
      params.firmId,
      params.accessCode,
    );
    return response;
  },
);

const casesSlice = createSlice({
  name: "cases",
  initialState,
  reducers: {
    clearCurrentCase: (state) => {
      state.currentCase = null;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch cases
      .addCase(fetchCases.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCases.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload.data;
        const formatedData = data?.map((values: any) => ({
          ...values,
          supervisor: values.supervisor?.full_name,
          customer: values.customer?.company,
          // Keep original lawyers_assigned intact for edit form
          // lawyers_assigned is already set from spread above
          files: `[${values.files?.length || 0} Files]`,
        }));

        state.cases = formatedData;
      })

      .addCase(fetchCases.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch cases";
      })
      // Fetch case by id
      .addCase(fetchCaseById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCaseById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCase = action.payload.data;
      })
      .addCase(fetchCaseById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch case";
      })
      // Create case
      .addCase(createCase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCase.fulfilled, (state, action) => {
        state.loading = false;
        state.cases.push(action.payload.data);
      })
      .addCase(createCase.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as any)?.message || action.error.message || "Failed to create case";
      })
      // Update case
      .addCase(updateCase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCase.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.cases.findIndex((c) => c.id === action.payload.id);
        if (index !== -1) {
          state.cases[index] = action.payload.data;
        }
        if (state.currentCase?.id === action.payload.id) {
          state.currentCase = action.payload;
        }
      })
      .addCase(updateCase.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as any)?.message || action.error.message || "Failed to update case";
      })
      // Delete case
      .addCase(deleteCase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCase.fulfilled, (state, action) => {
        state.loading = false;
        // Filter by order_id instead of id since we're returning orderId (string)
        state.cases = state.cases.filter(
          (c) => c.id.toString() !== action.payload,
        );
        if (state.currentCase?.id.toString() === action.payload) {
          state.currentCase = null;
        }
      })
      .addCase(deleteCase.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as any)?.message || action.error.message || "Failed to delete case";
      })
      // Update case status
      .addCase(updateCaseStatus.fulfilled, (state, action) => {
        const index = state.cases.findIndex((c) => c.id === action.payload.id);
        if (index !== -1) {
          state.cases[index] = action.payload;
        }
        if (state.currentCase?.id === action.payload.id) {
          state.currentCase = action.payload;
        }
      })
      .addCase(updateCaseStatus.rejected, (state, action) => {
        state.error = (action.payload as any)?.message || action.error.message || "Failed to update status";
      })
      // Fetch public case details
      .addCase(fetchPublicCaseDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPublicCaseDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCase = action.payload.data;
      })
      .addCase(fetchPublicCaseDetails.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to fetch public case details";
      });
  },
});

export const { clearCurrentCase, setError } = casesSlice.actions;

export default casesSlice.reducer;
